import { isEmpty, isUndefined } from "lodash";
import cleanComponents from "utils/cleanComponents";
import { getObjValAtPath } from "utils/fields";
import { calcBuildingSurfaceArea } from "utils/enclosure/geometry";

const codeRefMap = {
    Wall: {
        path: ["wallInsType.codeRef"],
        componentType: ["wall"],
    },
    BasementWall: {
        path: ["wall.intAddedInsType.codeRef"],
        componentType: ["basement"],
    },
    Ceiling: {
        path: ["ceilingInsType.codeRef"],
        componentType: ["ceiling"],
    },
    CeilingFlat: {
        path: ["ceilingInsType.codeRef"],
        componentType: ["ceiling"],
    },
    CrawlspaceWall: {
        path: ["wall.crawlspaceWallInsType.codeRef", "wall.ponyWall.ponyWallInsType.codeRef"],
        componentType: ["crawlspace", "basement"],
    },
    Floor: {
        path: ["expFloorInsType.codeRef"],
        componentType: ["expFloor"],
    },
    FloorsAbove: {
        path: ["floor.floorsAboveInsType.codeRef", "floor.floorsAboveInsType.codeRef"],
        componentType: ["basement", "crawlspace"],
    },
    FloorsAdded: {
        path: ["floor.slabInsType.codeRef", "floor.slabInsType.codeRef", "floor.slabInsType.codeRef"],
        componentType: ["basement", "crawlspace", "slab"],
    },
    Lintel: {
        path: ["lintelInsType.codeRef", "wall.lintelInsType.codeRef", "wall.lintelInsType.codeRef"],
        componentType: ["wall", "basement", "crawlspace"],
    },
};

// export const getMatchCodeRefs = (components, newCodeRef, editingCodeRef) => {
//     const { path = '', componentType = '' } = codeRefMap[newCodeRef.split('-')[0]];

//     if (isEmpty(components[componentType]) || isEmpty(editingCodeRef)) {
//         return [];
//     }

//     return Object.keys(components[componentType]).reduce((cache, compKey) => {
//         const foundCodeRef = path.split('.').reduce((acc, curr) => acc[curr], components[componentType][compKey]);
//         if (foundCodeRef === editingCodeRef) {
//             return [...cache, `model.modelData.components.${componentType}.${compKey}.${path.split('.codeRef')[0]}`];
//         } else {
//             return cache;
//         }
//     }, []);
// };

export const getMatchCodeRefs = (components, newCodeRef, editingCodeRef) => {
    if (newCodeRef.includes("FloorHeader") || editingCodeRef.includes("FloorHeader")) {
        return getMatchFloorHeaderCodeRefs(components, editingCodeRef);
    }

    const mapEntry = codeRefMap[newCodeRef.split("-")[0]];
    if (!mapEntry) {
        return [];
    }

    const { path: pathArray = [], componentType = [] } = mapEntry;

    const accessorList = pathArray.reduce((acc, path, ind) => {
        if (isEmpty(components[componentType[ind]]) || isEmpty(editingCodeRef)) {
            return acc;
        }

        return [
            ...acc,
            ...Object.keys(components[componentType[ind]]).reduce((cache, compKey) => {
                const root = components[componentType[ind]][compKey];
                const foundCodeRef = path
                    .split(".")
                    .reduce((walkAcc, curr) => walkAcc?.[curr], root);
                if (foundCodeRef === editingCodeRef) {
                    return [
                        ...cache,
                        `model.modelData.components.${componentType[ind]}.${compKey}.${path.split(".codeRef")[0]}`,
                    ];
                } else {
                    return cache;
                }
            }, []),
        ];
    }, []);

    return accessorList;
};

export const getMatchFloorHeaderCodeRefs = (components, editingCodeRef) => {
    const { wall = {}, basement = {}, crawlspace = {} } = components;

    let accessorsToUpdate = [];
    if (!isEmpty(wall)) {
        Object.entries(wall).forEach(([wallKey, wallObj]) => {
            const { subcomponents: { floorHeader = {} } = {} } = wallObj;

            if (!isEmpty(floorHeader)) {
                Object.keys(floorHeader).forEach((floorHeaderKey) => {
                    const { floorHeaderInsType: { codeRef: floorHeaderCodeRef = "" } = {} } =
                        floorHeader[floorHeaderKey] || {};

                    if (floorHeaderCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.wall.${wallKey}.subcomponents.floorHeader.${floorHeaderKey}.floorHeaderInsType`,
                        ];
                    }
                });
            }
        });
    }

    if (!isEmpty(basement)) {
        Object.entries(basement).forEach(([basementKey, basementObj]) => {
            const { subcomponents: { floorHeader = {} } = {} } = basementObj;

            if (!isEmpty(floorHeader)) {
                Object.keys(floorHeader).forEach((floorHeaderKey) => {
                    const { floorHeaderInsType: { codeRef: floorHeaderCodeRef = "" } = {} } =
                        floorHeader[floorHeaderKey] || {};

                    if (floorHeaderCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.basement.${basementKey}.subcomponents.floorHeader.${floorHeaderKey}.floorHeaderInsType`,
                        ];
                    }
                });
            }
        });
    }

    if (!isEmpty(crawlspace)) {
        Object.entries(crawlspace).forEach(([crawlspaceKey, crawlspaceObj]) => {
            const { subcomponents: { floorHeader = {} } = {} } = crawlspaceObj;

            if (!isEmpty(floorHeader)) {
                Object.keys(floorHeader).forEach((floorHeaderKey) => {
                    const { floorHeaderInsType: { codeRef: floorHeaderCodeRef = "" } = {} } =
                        floorHeader[floorHeaderKey] || {};

                    if (floorHeaderCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.crawlspace.${crawlspaceKey}.subcomponents.floorHeader.${floorHeaderKey}.floorHeaderInsType`,
                        ];
                    }
                });
            }
        });
    }

    return accessorsToUpdate;
};

export const getAllComponents = (componentType, components, currentComponent = "") =>
    Object.keys(components)
        .reduce((cache, key) => {
            // get id and location
            const cleanedComps = cleanComponents(components);
            const parentComp = cleanedComps[key] || {};

            let componentArray = Object.keys(parentComp).map((componentId) => {
                const { label = "", constructionType: { id: constructionTypeId } = {} } = parentComp[componentId] || {};
                const value = `${key}.${componentId}`;

                const isCurrentComponent = currentComponent !== "" && currentComponent.includes(value);

                return {
                    label,
                    value,
                    isFlat: [2, 3].includes(constructionTypeId),
                    disabled: isCurrentComponent,
                };
            });

            if (componentType === "ceiling-flat") {
                componentArray = componentArray.filter(({ isFlat }) => isFlat);
            } else if (componentType === "ceiling") {
                componentArray = componentArray.filter(({ isFlat }) => !isFlat);
            }

            return [...cache, ...componentArray];
        }, [])
        .filter(({ value = "" }) => value.includes(componentType.split("-")[0])); //splitting handles "ceiling-flat"

//Only call this with 'component' === 'window' 'door' or 'floorHeader'
export const getAllSubcomponents = (componentType, components, currentComponent = "") =>
    Object.keys(components)
        .reduce((cache, key) => {
            const cleanedComps = cleanComponents(components);
            //this returns a flat array with value (accessor) and label of subcomponents

            const parentComp = cleanedComps[key] || {};
            const subcomponents = Object.keys(parentComp).reduce((compCache, parentKey) => {
                const { subcomponents: { [componentType]: subComps = {}, door: doorObj = {} } = {} } =
                    parentComp[parentKey] || {};
                return [
                    ...compCache,
                    ...Object.keys(subComps).reduce((subCache, subCompKey) => {
                        return [
                            ...subCache,
                            {
                                value: `${key}.${parentKey}.subcomponents.${componentType}.${subCompKey}`,
                                label: subComps[subCompKey].label,
                            },
                        ];
                    }, []),
                    ...(componentType === "window"
                        ? getWindowsOnDoors(doorObj, `${key}.${parentKey}.subcomponents`)
                        : []),
                ];
            }, []);

            const subComponentArray = subcomponents.map((subcomp) => {
                const { label = "", value = "" } = subcomp;

                const isCurrentComponent = currentComponent !== "" && currentComponent.includes(value);

                return {
                    label,
                    value,
                    disabled: isCurrentComponent,
                };
            });

            return [...cache, ...subComponentArray];
        }, [])
        .filter(({ value = "" }) => value.includes(componentType));

const getWindowsOnDoors = (doorsObj, currentPath) => {
    return Object.keys(doorsObj).reduce((cache, doorKey) => {
        const { subcomponents: { window = {} } = {} } = doorsObj[doorKey] || {};
        return [
            ...cache,
            ...Object.keys(window).reduce((subCache, subCompKey) => {
                return [
                    ...subCache,
                    {
                        value: `${currentPath}.door.${doorKey}.subcomponents.window.${subCompKey}`,
                        label: window[subCompKey].label,
                    },
                ];
            }, []),
        ];
    }, []);
};

export const getSubComponentRenderProps = ({
    componentType = "",
    parentAccessor = "",
    parentPath = "",
} = {}) => {
    if (!["door", "floorHeader", "window"].includes(componentType)) {
        return {};
    }

    const isSubSubComponent = componentType === "window" && parentPath.includes(".door.");

    return {
        parentAccessor,
        parentPath,
        isSubComponent: !isSubSubComponent,
        ...(isSubSubComponent ? { isSubSubComponent: true } : {}),
    };
};

export const getCompAreaForCostCalc = (selectedComponents = [], components = {}, compType = "", calcType = "") => {
    //Where selectedComponents is an array of accessors like ["ceiling.ceiling001", "ceiling.ceiling002"]
    //components is the object at modelData.components
    //compType aligns with "upgradeType"

    if (compType === "airTightness") {
        return calcBuildingSurfaceArea(components, true);
    }

    if (isEmpty(selectedComponents) || isEmpty(components) || compType === "") return 0;

    return selectedComponents
        .map((compAccessor) => {
            if (["basementSlab", "slab"].includes(compType)) {
                const { measurements: { isRectangular, length = 0, width = 0, area = 0 } = {} } =
                    getObjValAtPath(components, `${compAccessor}.floor`) || {};

                return isRectangular ? length * width : area;
            } else if (["basementWall", "crawlspace"].includes(compType)) {
                const { measurements: { isRectangular, length = 0, width = 0, perimeter = 0 } = {} } =
                    getObjValAtPath(components, `${compAccessor}.floor`) || {};
                const { measurements: { height = 0 } = {} } = getObjValAtPath(components, `${compAccessor}.wall`) || {};

                return isRectangular ? height * 2 * (length + width) : height * perimeter;
            } else if (["window", "skylights"].includes(compType)) {
                const { numIdentical = 1, measurements: { width = 0, height = 0 } = {} } =
                    getObjValAtPath(components, compAccessor) || {};
                if (calcType === "matCostArea") {
                    return (numIdentical * (width * height)) / 1000000; //mm2 to m2
                } else {
                    //By default, window returns count
                    return 1 * numIdentical;
                }
            } else if (compType === "door") {
                const { measurements: { width = 0, height = 0 } = {} } =
                    getObjValAtPath(components, compAccessor) || {};
                if (calcType === "matCostArea") {
                    return width * height; //area
                } else {
                    return 1; //count
                }
            }

            const { measurements: { area } = {} } = getObjValAtPath(components, compAccessor) || {};

            return area;
        })
        .reduce((sum, curr) => sum + curr, 0);
};

export const calcWindowToWallRatio = (components = {}) => {
    const { wall = {}, basement = {}, crawlspace = {} } = components;

    let totalAgWallArea = 0;
    let totalBsmtCrwlAgWallArea = 0;
    let totalFloorHeaderArea = 0;
    let totalWindowArea = 0;
    let totalDoorArea = 0;
    Object.keys(basement).forEach((bsmtKey) => {
        const {
            exposedSurfacePerimeter = 0,
            // floor: { isRectangular = true, area = 0, width = 0, perimeter = 0, length = 0 } = {},
            wall: { measurements: { height = 0, depth = 0 } = {} } = {},
            subcomponents: {
                window: windowsOnWall = {},
                door: doorsOnWall = {},
                floorHeader: floorHeaderOnWall = {},
            } = {},
        } = basement[bsmtKey] || {};

        const totalExposedPerimeter = exposedSurfacePerimeter;
        const currentAgArea = totalExposedPerimeter * (height - depth);
        totalBsmtCrwlAgWallArea = totalBsmtCrwlAgWallArea + currentAgArea;

        Object.keys(windowsOnWall).forEach((windowKey) => {
            const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                windowsOnWall[windowKey] || {};
            const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

            totalWindowArea = totalWindowArea + currentWinArea;
        });

        Object.keys(doorsOnWall).forEach((doorKey) => {
            const {
                measurements: { height: doorHeight = 0, width: doorWidth = 0 } = {},
                subcomponents: { window: windowsOnDoor = {} } = {},
            } = doorsOnWall[doorKey] || {};
            totalDoorArea = totalDoorArea + doorHeight * doorWidth; // [area in m2]

            Object.keys(windowsOnDoor).forEach((windowKey) => {
                const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                    windowsOnDoor[windowKey] || {};
                const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

                totalWindowArea = totalWindowArea + currentWinArea;
            });
        });

        Object.keys(floorHeaderOnWall).forEach((fhKey) => {
            const { measurements: { height: fhHeight = 0, perimeter: fhPerimeter = 0 } = {} } =
                floorHeaderOnWall[fhKey] || {};
            totalFloorHeaderArea = totalFloorHeaderArea + fhHeight * fhPerimeter; // [area in m2]
        });
    });

    Object.keys(crawlspace).forEach((crwlKey) => {
        const {
            exposedSurfacePerimeter = 0,
            // floor: { isRectangular = true, area = 0, width = 0, perimeter = 0, length = 0 } = {},
            wall: { measurements: { height = 0, depth = 0 } = {} } = {},
            subcomponents: {
                window: windowsOnWall = {},
                door: doorsOnWall = {},
                floorHeader: floorHeaderOnWall = {},
            } = {},
        } = crawlspace[crwlKey] || {};

        const totalExposedPerimeter = exposedSurfacePerimeter;
        const currentAgArea = totalExposedPerimeter * (height - depth);
        totalBsmtCrwlAgWallArea = totalBsmtCrwlAgWallArea + currentAgArea;

        Object.keys(windowsOnWall).forEach((windowKey) => {
            const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                windowsOnWall[windowKey] || {};
            const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

            totalWindowArea = totalWindowArea + currentWinArea;
        });

        Object.keys(doorsOnWall).forEach((doorKey) => {
            const {
                measurements: { height: doorHeight = 0, width: doorWidth = 0 } = {},
                subcomponents: { window: windowsOnDoor = {} } = {},
            } = doorsOnWall[doorKey] || {};
            totalDoorArea = totalDoorArea + doorHeight * doorWidth; // [area in m2]

            Object.keys(windowsOnDoor).forEach((windowKey) => {
                const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                    windowsOnDoor[windowKey] || {};
                const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

                totalWindowArea = totalWindowArea + currentWinArea;
            });
        });

        Object.keys(floorHeaderOnWall).forEach((fhKey) => {
            const { measurements: { height: fhHeight = 0, perimeter: fhPerimeter = 0 } = {} } =
                floorHeaderOnWall[fhKey] || {};
            totalFloorHeaderArea = totalFloorHeaderArea + fhHeight * fhPerimeter; // [area in m2]
        });
    });

    Object.keys(wall).forEach((wallKey) => {
        const {
            measurements: { height = 0, perimeter = 0 } = {},
            subcomponents: {
                window: windowsOnWall = {},
                door: doorsOnWall = {},
                floorHeader: floorHeaderOnWall = {},
            } = {},
        } = wall[wallKey] || {};

        const currWallArea = height * perimeter;
        totalAgWallArea = totalAgWallArea + currWallArea;

        Object.keys(windowsOnWall).forEach((windowKey) => {
            const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                windowsOnWall[windowKey] || {};
            const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

            totalWindowArea = totalWindowArea + currentWinArea;
        });

        Object.keys(doorsOnWall).forEach((doorKey) => {
            const {
                measurements: { height: doorHeight = 0, width: doorWidth = 0 } = {},
                subcomponents: { window: windowsOnDoor = {} } = {},
            } = doorsOnWall[doorKey] || {};
            totalDoorArea = totalDoorArea + doorHeight * doorWidth; // [area in m2]

            Object.keys(windowsOnDoor).forEach((windowKey) => {
                const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                    windowsOnDoor[windowKey] || {};
                const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

                totalWindowArea = totalWindowArea + currentWinArea;
            });
        });

        Object.keys(floorHeaderOnWall).forEach((fhKey) => {
            const { measurements: { height: fhHeight = 0, perimeter: fhPerimeter = 0 } = {} } =
                floorHeaderOnWall[fhKey] || {};
            totalFloorHeaderArea = totalFloorHeaderArea + fhHeight * fhPerimeter; // [area in m2]
        });
    });

    let wwr = totalWindowArea / (totalAgWallArea + totalBsmtCrwlAgWallArea + totalFloorHeaderArea);

    return wwr;
};

export const defaultComponentFilter = ["basement", "crawlspace", "slab", "ceiling", "wall", "expFloor", "door", "window", "floorHeader"];
export const defaultDirectionFilter = ["NA", "S", "SE", "E", "NE", "N", "NW", "W", "SW"];

export const DIRECTIONAL_COMPONENT_TYPES = ["wall", "window", "door", "floorHeader"];
export const HORIZONTAL_DIRECTION_KEY = "HORIZONTAL";
export const HORIZONTAL_COMPONENT_TYPES = ["basement", "crawlspace", "slab", "ceiling", "expFloor"];
export const COMPASS_DIRECTION_ORDER = ["S", "SE", "E", "NE", "N", "NW", "W", "SW"];
export const DIRECTION_FILTER_LABELS = {
    NA: "N/A",
    HORIZONTAL: "Horizontal",
    S: "South",
    SE: "Southeast",
    E: "East",
    NE: "Northeast",
    N: "North",
    NW: "Northwest",
    W: "West",
    SW: "Southwest",
};

const COMPASS_DIRECTION_KEYS = COMPASS_DIRECTION_ORDER;

export const isHorizontalComponentType = (componentType = "") => HORIZONTAL_COMPONENT_TYPES.includes(componentType);

/** Empty selection ("None") is treated as N/A. */
export const normalizeDirectionFilterValue = (value = []) =>
    !value?.length ? ["NA"] : value;

/** True when the direction filter is the None / N/A-only state. */
export const isDirectionFilterNone = (directionFilter = []) => {
    const normalized = normalizeDirectionFilterValue(directionFilter);
    return normalized.length === 1 && normalized[0] === "NA";
};

export const getDefaultDirectionFilterForOptions = (options = []) => options.map(({ value }) => value);

let previousAvailableDirectionValues = null;
let lastSyncedDirectionFilterKey = null;
let lastSyncedDirectionFilterResult = null;
let trackedModelId = null;

export const resetDirectionFilterAvailabilityTracking = () => {
    previousAvailableDirectionValues = null;
    lastSyncedDirectionFilterKey = null;
    lastSyncedDirectionFilterResult = null;
};

export const setDirectionFilterTrackingModelId = (modelId) => {
    if (modelId == null || trackedModelId === modelId) {
        return;
    }

    trackedModelId = modelId;
    resetDirectionFilterAvailabilityTracking();
};

/**
 * Keeps the direction filter aligned when new orientations appear in the model.
 * Newly present directions are always added automatically so that components
 * added with a new direction remain visible regardless of the current filter.
 */
export const syncDirectionFilterWithAvailable = (saved = [], availableValues = []) => {
    const syncKey = `${availableValues.join(",")}|${(saved || []).join(",")}`;

    if (syncKey === lastSyncedDirectionFilterKey) {
        return lastSyncedDirectionFilterResult;
    }

    const sanitized = sanitizeDirectionFilter(saved, availableValues);
    const previousAvailable = previousAvailableDirectionValues;
    let result = sanitized;

    if (previousAvailable?.length && availableValues?.length) {
        const newDirections = availableValues.filter((value) => !previousAvailable.includes(value));

        // Always add newly-appeared directions to the filter so that
        // components added with a new direction remain visible regardless
        // of whether all directions were previously selected.
        if (newDirections.length) {
            result = [...sanitized];

            newDirections.forEach((value) => {
                if (!result.includes(value)) {
                    result.push(value);
                }
            });
        }
    }

    previousAvailableDirectionValues = availableValues?.length ? [...availableValues] : [];
    lastSyncedDirectionFilterKey = syncKey;
    lastSyncedDirectionFilterResult = result;

    return result;
};

export const getEffectiveDirectionFilter = (savedFilter = [], availableValues = []) => {
    const available = availableValues?.length ? availableValues : defaultDirectionFilter;
    return normalizeDirectionFilterValue(syncDirectionFilterWithAvailable(savedFilter, available));
};

export const isDirectionFilterApplied = (directionFilter = [], availableValues = []) => {
    if (!availableValues?.length) {
        return false;
    }

    const filter = getEffectiveDirectionFilter(directionFilter, availableValues);
    const available = availableValues;

    return filter.length !== available.length || !available.every((value) => filter.includes(value));
};

/** Parse component type from a model.components.*.facingDirection change path. */
export const parseComponentFacingDirectionPath = (path = "") => {
    if (!path.endsWith(".facingDirection")) {
        return null;
    }

    const prefix = "model.modelData.components.";
    if (!path.startsWith(prefix)) {
        return null;
    }

    const relative = path.slice(prefix.length);
    const parts = relative.split(".");

    if (parts.length < 3) {
        return null;
    }

    if (parts.includes("subcomponents")) {
        const subIdx = parts.lastIndexOf("subcomponents");
        const componentType = parts[subIdx + 1];
        return DIRECTIONAL_COMPONENT_TYPES.includes(componentType) ? componentType : null;
    }

    const componentType = parts[0];
    return DIRECTIONAL_COMPONENT_TYPES.includes(componentType) ? componentType : null;
};

/** Add a direction to the saved filter when missing so edited components stay visible. */
export const ensureDirectionInFilter = (savedFilter = [], directionKey, availableValues = []) => {
    if (!directionKey || !availableValues?.length) {
        return savedFilter;
    }

    const sanitized = sanitizeDirectionFilter(savedFilter, availableValues);

    if (!availableValues.includes(directionKey) || sanitized.includes(directionKey)) {
        return sanitized;
    }

    return [...sanitized, directionKey];
};

export const sanitizeDirectionFilter = (saved = [], availableValues = []) => {
    if (!availableValues?.length) {
        return [];
    }

    const isLegacyDefault =
        saved?.length === defaultDirectionFilter.length &&
        defaultDirectionFilter.every((value) => saved.includes(value));

    if (!saved?.length || isLegacyDefault) {
        return [...availableValues];
    }

    const sanitized = saved.filter((value) => availableValues.includes(value));

    if (!sanitized.length) {
        return availableValues.includes("NA") ? ["NA"] : [...availableValues];
    }

    return sanitized;
};

const collectDirectionKeysFromSubcomponents = (subcomponentsObject = {}, presentKeys = new Set()) => {
    const { window = {}, door = {}, floorHeader = {} } = subcomponentsObject;

    Object.values(window).forEach((component) => {
        if (!component || isEmpty(component)) return;

        const key = normalizeFacingDirectionKey("window", component.facingDirection?.id);
        presentKeys.add(key ?? "NA");
    });

    Object.values(floorHeader).forEach((component) => {
        if (!component || isEmpty(component)) return;

        const key = normalizeFacingDirectionKey("floorHeader", component.facingDirection?.id);
        if (key) presentKeys.add(key);
    });

    Object.values(door).forEach((component) => {
        if (!component || isEmpty(component)) return;

        const key = normalizeFacingDirectionKey("door", component.facingDirection?.id);
        presentKeys.add(key ?? "NA");

        collectDirectionKeysFromSubcomponents(component.subcomponents, presentKeys);
    });
};

const collectDirectionKeysFromComponentType = (componentsByType = {}, componentType = "", presentKeys = new Set()) => {
    Object.values(componentsByType).forEach((component) => {
        if (!component || isEmpty(component)) return;

        if (isHorizontalComponentType(componentType)) {
            presentKeys.add(HORIZONTAL_DIRECTION_KEY);
        }

        if (componentType === "wall") {
            const facingId = component.facingDirection?.id;
            const key = normalizeFacingDirectionKey("wall", facingId);
            if (key) presentKeys.add(key);
        }

        collectDirectionKeysFromSubcomponents(component.subcomponents, presentKeys);
    });
};

export const collectPresentDirectionKeys = (componentsObject = {}) => {
    const presentKeys = new Set();
    const {
        basement = {},
        crawlspace = {},
        slab = {},
        wall = {},
        ceiling = {},
        expFloor = {},
    } = componentsObject;

    collectDirectionKeysFromComponentType(basement, "basement", presentKeys);
    collectDirectionKeysFromComponentType(crawlspace, "crawlspace", presentKeys);
    collectDirectionKeysFromComponentType(slab, "slab", presentKeys);
    collectDirectionKeysFromComponentType(ceiling, "ceiling", presentKeys);
    collectDirectionKeysFromComponentType(expFloor, "expFloor", presentKeys);
    collectDirectionKeysFromComponentType(wall, "wall", presentKeys);

    return presentKeys;
};

export const getAvailableDirectionFilterOptions = (componentsObject = {}) => {
    const presentKeys = collectPresentDirectionKeys(componentsObject);
    const options = [];

    if (presentKeys.has(HORIZONTAL_DIRECTION_KEY)) {
        options.push({ value: HORIZONTAL_DIRECTION_KEY, label: DIRECTION_FILTER_LABELS.HORIZONTAL });
    }

    COMPASS_DIRECTION_ORDER.forEach((key) => {
        if (presentKeys.has(key)) {
            options.push({ value: key, label: DIRECTION_FILTER_LABELS[key] });
        }
    });

    if (presentKeys.has("NA")) {
        options.push({ value: "NA", label: DIRECTION_FILTER_LABELS.NA });
    }

    return options;
};

/**
 * Normalize a component's own facingDirection.id to a direction filter key.
 * Walls/floor headers: 0 = N/A, 1–8 = compass.
 * Windows/doors: 0–7 = compass (no N/A).
 */
export const normalizeFacingDirectionKey = (componentType, facingId) => {
    if (facingId == null || Number.isNaN(facingId)) return null;

    if (componentType === "wall" || componentType === "floorHeader") {
        if (facingId === 0) return "NA";
        return COMPASS_DIRECTION_KEYS[facingId - 1] || null;
    }

    if (componentType === "window" || componentType === "door") {
        return COMPASS_DIRECTION_KEYS[facingId] || null;
    }

    return null;
};

/** Direction filter key for a component's own data (horizontal types, compass key, or N/A). */
export const getComponentDirectionKey = (componentType, componentData = {}) =>
    isHorizontalComponentType(componentType)
        ? HORIZONTAL_DIRECTION_KEY
        : normalizeFacingDirectionKey(componentType, componentData?.facingDirection?.id) ?? "NA";

/** Map of every component path (including nested subcomponents) to its direction filter key. */
export const collectComponentDirectionKeysByPath = (componentsObject = {}, prefix = "", result = {}) => {
    Object.entries(componentsObject || {}).forEach(([componentType, componentsByType]) => {
        if (!componentsByType || typeof componentsByType !== "object") return;

        Object.entries(componentsByType).forEach(([componentId, component]) => {
            if (!component || typeof component !== "object" || isEmpty(component)) return;

            const path = `${prefix}${componentType}.${componentId}`;
            result[path] = getComponentDirectionKey(componentType, component);

            if (component.subcomponents) {
                collectComponentDirectionKeysByPath(component.subcomponents, `${path}.subcomponents.`, result);
            }
        });
    });

    return result;
};

export const WALL_COMPASS_FACING_IDS = [1, 2, 3, 4, 5, 6, 7, 8];
export const DEFAULT_HOUSE_FACING_ID = 4;

/** True when a wall has a compass direction assigned (not N/A). */
export const wallHasAssignedDirection = (facingId) => WALL_COMPASS_FACING_IDS.includes(facingId);

/** True when a door has a compass direction assigned (not undefined). */
export const doorHasAssignedDirection = (facingId) => !isUndefined(facingId);

/** House facingDirection.id (0–7), falling back to North when missing or invalid. */
export const resolveHouseFacingId = (houseFacingId) => {
    if (houseFacingId == null || Number.isNaN(houseFacingId) || houseFacingId < 0 || houseFacingId > 7) {
        return DEFAULT_HOUSE_FACING_ID;
    }

    return houseFacingId;
};

/** Map house facingDirection.id (0–7) to a subcomponent facingDirection.id. */
export const convertHouseFacingIdToSubcomponent = (componentType, houseFacingId) => {
    const id = resolveHouseFacingId(houseFacingId);

    if (componentType === "window" || componentType === "door") {
        return id;
    }

    if (componentType === "floorHeader") {
        return id + 1;
    }

    return null;
};

/** Apply house direction to newly created subcomponent data (wall is N/A). */
export const applyHouseDirectionToNewSubcomponent = (componentData, componentType, houseFacingId) => {
    const converted = convertHouseFacingIdToSubcomponent(componentType, houseFacingId);
    if (converted == null) {
        return componentData;
    }

    return {
        ...componentData,
        facingDirection: { id: converted },
    };
};

/** Apply a wall's compass direction to newly created subcomponent data. */
export const applyWallDirectionToNewSubcomponent = (componentData, componentType, wallFacingId) => {
    const inheritedId = convertWallFacingIdToSubcomponent(componentType, wallFacingId);
    if (inheritedId == null) {
        return componentData;
    }

    return {
        ...componentData,
        facingDirection: { id: inheritedId },
    };
};

/** Map a wall facingDirection.id to the equivalent subcomponent id. */
export const convertWallFacingIdToSubcomponent = (componentType, wallFacingId) => {
    if (!wallHasAssignedDirection(wallFacingId)) {
        return null;
    }

    if (componentType === "window" || componentType === "door") {
        return wallFacingId - 1;
    }

    if (componentType === "floorHeader") {
        return wallFacingId;
    }

    return null;
};

const applyFacingDirectionToSubcomponents = (subcomponents = {}, convertFn) => {
    const { window = {}, door = {}, floorHeader = {} } = subcomponents;

    Object.values(window).forEach((component) => {
        if (!component?.facingDirection) return;

        const converted = convertFn("window");
        if (converted != null) {
            component.facingDirection.id = converted;
        }
    });

    Object.values(floorHeader).forEach((component) => {
        if (!component?.facingDirection) return;

        const converted = convertFn("floorHeader");
        if (converted != null) {
            component.facingDirection.id = converted;
        }
    });

    Object.values(door).forEach((component) => {
        if (!component?.facingDirection) return;

        const converted = convertFn("door");
        if (converted != null) {
            component.facingDirection.id = converted;
        }

        Object.values(component.subcomponents?.window || {}).forEach((win) => {
            if (!win?.facingDirection) return;

            const winConverted = convertFn("window");
            if (winConverted != null) {
                win.facingDirection.id = winConverted;
            }
        });
    });
};

const applyWallDirectionToSubcomponents = (subcomponents = {}, wallFacingId) =>
    applyFacingDirectionToSubcomponents(subcomponents, (componentType) =>
        convertWallFacingIdToSubcomponent(componentType, wallFacingId)
    );

/**
 * When a wall gets a compass direction, propagate it to all subcomponents.
 * When the wall is set to N/A, seed subcomponents from the wall's previous compass direction.
 */
export const syncWallSubcomponentDirections = (
    draft,
    path,
    facingDirectionValue,
    previousFacingDirectionValue
) => {
    if (parseComponentFacingDirectionPath(path) !== "wall") {
        return;
    }

    const wallFacingId = facingDirectionValue?.id;

    const prefix = "model.modelData.components.wall.";
    if (!path.startsWith(prefix)) {
        return;
    }

    const wallKey = path.slice(prefix.length).replace(/\.facingDirection$/, "");
    const wall = draft?.model?.modelData?.components?.wall?.[wallKey];
    if (!wall?.subcomponents) {
        return;
    }

    if (wallHasAssignedDirection(wallFacingId)) {
        applyWallDirectionToSubcomponents(wall.subcomponents, wallFacingId);
        return;
    }

    if (wallFacingId === 0) {
        const previousWallFacingId = previousFacingDirectionValue?.id;

        if (wallHasAssignedDirection(previousWallFacingId)) {
            applyWallDirectionToSubcomponents(wall.subcomponents, previousWallFacingId);
        }
    }
};

/**
 * When a door direction changes, propagate it to windows on that door.
 * Skipped when the parent wall has an assigned direction (wall sync is the source of truth).
 */
export const syncDoorSubcomponentDirections = (draft, path, facingDirectionValue) => {
    if (parseComponentFacingDirectionPath(path) !== "door") {
        return;
    }

    const doorFacingId = facingDirectionValue?.id;
    if (doorFacingId == null || Number.isNaN(doorFacingId)) {
        return;
    }

    const prefix = "model.modelData.components.";
    if (!path.startsWith(prefix)) {
        return;
    }

    const relativePath = path.slice(prefix.length).replace(/\.facingDirection$/, "");
    const parts = relativePath.split(".");

    if (parts[0] === "wall" && parts.length >= 4) {
        const wallKey = parts[1];
        const wall = draft?.model?.modelData?.components?.wall?.[wallKey];
        if (wallHasAssignedDirection(wall?.facingDirection?.id)) {
            return;
        }
    }

    const door = getObjValAtPath(draft?.model?.modelData?.components, relativePath);
    const windows = door?.subcomponents?.window || {};

    Object.values(windows).forEach((win) => {
        if (win?.facingDirection) {
            win.facingDirection.id = doorFacingId;
        }
    });
};

/** True when a subcomponent's direction is inherited from its parent wall/door and should not be edited. */
export const isSubcomponentDirectionLocked = (
    componentType,
    { mainCompFacingId, parentFacingId, isDoorWindow } = {}
) => {
    if (isDoorWindow) {
        return doorHasAssignedDirection(parentFacingId) || wallHasAssignedDirection(mainCompFacingId);
    }
    return wallHasAssignedDirection(mainCompFacingId);
};

/**
 * Match components whose stored facingDirection (or horizontal type) is in the filter.
 * Components with undefined/null facingDirection are treated as "N/A".
 */
export const matchesDirectionFilter = (
    componentType,
    facingId,
    directionFilter = defaultDirectionFilter,
    _ancestors = [],
    availableValues
) => {
    const available = availableValues?.length ? availableValues : defaultDirectionFilter;

    if (!isDirectionFilterApplied(directionFilter, available)) {
        return true;
    }

    const filter = getEffectiveDirectionFilter(directionFilter, available);

    if (isHorizontalComponentType(componentType)) {
        return filter.includes(HORIZONTAL_DIRECTION_KEY);
    }

    if (!DIRECTIONAL_COMPONENT_TYPES.includes(componentType)) {
        return false;
    }

    const key = normalizeFacingDirectionKey(componentType, facingId);
    const effectiveKey = key ?? "NA";
    return filter.includes(effectiveKey);
};
